/**
 * 运镜库面板 — 展示预设 + 自定义运镜方案
 */
import { useState } from 'react';
import { Plus, ChevronRight, Tag } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { CreateCameraMoveDialog } from './CreateCameraMoveDialog';
import type { BrainAsset, BrainAssetListResponse, CameraMoveAttributes, CameraMoveStep } from './types';

async function fetchCameraMoves(): Promise<BrainAssetListResponse> {
  return await api
    .get('brain/assets', { searchParams: { kind: 'camera_move', limit: 200 } })
    .json<BrainAssetListResponse>();
}

function StepFlow({ steps }: { steps: CameraMoveStep[] }) {
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs">
      {steps.map((s, i) => (
        <span key={s.stepNo} className="flex items-center gap-1">
          <span className="rounded bg-neutral-100 px-1.5 py-0.5 font-medium text-neutral-700">
            {s.shotType}
          </span>
          <span className="text-neutral-400">{s.cameraMovement}</span>
          {i < steps.length - 1 && <ChevronRight className="h-3 w-3 text-neutral-300" />}
        </span>
      ))}
    </div>
  );
}

export function CameraMovePanel() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BrainAsset | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['brain', 'assets', 'camera_move'],
    queryFn: fetchCameraMoves,
    placeholderData: (prev) => prev,
  });

  const deleteMut = useMutation({
    mutationFn: async (assetId: string) => {
      await api.delete(`brain/assets/${assetId}`).json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['brain', 'assets', 'camera_move'] });
      toast.success('已删除');
    },
    onError: (e: Error) => toast.error('删除失败', e.message || '网络异常'),
  });

  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-neutral-500">
          预设与自定义运镜方案,制作分镜时可引用
        </p>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          新建运镜方案
        </Button>
      </div>

      {isLoading && !data && (
        <div className="py-16 text-center text-sm text-neutral-400">加载中…</div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="py-16 text-center text-sm text-neutral-400">
          暂无运镜方案,点击右上角「新建」创建
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((asset) => {
          const attrs = (asset.attributes ?? {}) as unknown as CameraMoveAttributes;
          const isPreset = attrs.isPreset === true;
          return (
            <div
              key={asset.assetId}
              className={cn(
                'group relative rounded-lg border bg-white p-4 shadow-sm transition-shadow hover:shadow-md',
                isPreset && 'border-blue-200 bg-blue-50/30',
              )}
            >
              <div className="mb-2 flex items-center gap-2">
                <h3 className="font-semibold text-neutral-900">{asset.name}</h3>
                {isPreset && (
                  <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                    内置
                  </span>
                )}
              </div>

              {attrs.description && (
                <p className="mb-3 line-clamp-2 text-xs text-neutral-500">
                  {attrs.description}
                </p>
              )}

              <StepFlow steps={attrs.movementSequence ?? []} />

              {(attrs.suitableScenes?.length ?? 0) > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {attrs.suitableScenes!.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-0.5 rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] text-neutral-600"
                    >
                      <Tag className="h-2.5 w-2.5" />
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {!isPreset && (
                <button
                  type="button"
                  className="absolute right-2 top-2 rounded p-1 text-neutral-300 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"
                  onClick={() => setDeleteTarget(asset)}
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          );
        })}
      </div>

      <CreateCameraMoveDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => qc.invalidateQueries({ queryKey: ['brain', 'assets', 'camera_move'] })}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title={`删除「${deleteTarget?.name}」`}
        description="删除后不可恢复,确定继续?"
        confirmText="删除"
        tone="danger"
        pending={deleteMut.isPending}
        onConfirm={() => {
          if (deleteTarget) deleteMut.mutate(deleteTarget.assetId);
          setDeleteTarget(null);
        }}
      />
    </div>
  );
}
